
import { computed, defineComponent, onUnmounted, PropType, ref, watch } from 'vue';
import AddressPicker from '@/components/address-picker/index.vue';
import {
  PRODUCTION_PATTERN_ENUM,
  PICK_WAY_LIST,
  SEASON_LIST,
  PRODUCTION_MODELS_LIST,
  CLOTHES_UNIT_LIST,
  QUALITY_LEVEL_LIST,
} from '@/constant/production-demand';
import { IdictValuesItemNode } from '@/hooks/use-cascader';
import { useFuzzyCustomerByNameCode, useFuzzyCustomerDetail } from '../composable/use-fuzzy-customer';
import { useFuzzyFetchDesigner } from '../composable/use-fuzzy-fetch-designer';
import { IFile, IFileData } from '@/components/uploader/packages/types';
import { ElMessage } from 'element-plus';
import { IPurchaserDetailBaseInfo, IPurchaserListListItem, IV1ProdDemandRes } from '@/api/production-demand/type';
import { YES_OR_NO_NUMBER_ENUM } from '@/constant';

export interface IMachineData extends IV1ProdDemandRes {
  _styleType: string[];
  styleColor: string[];
  _stylePictureList: IFile[];
  designFileUrlList: IFile[];
}

export default defineComponent({
  components: {
    AddressPicker,
  },
  props: {
    modelValue: {
      type: Object as PropType<IMachineData>,
      required: true,
    },
    // 款式品类
    categoryTree: {
      type: Array as PropType<IdictValuesItemNode[]>,
      default: () => [],
    },
    // 标准尺码
    clothesSize: {
      type: Array as PropType<IdictValuesItemNode[]>,
      default: () => [],
    },
    // 品质等级
    qualityLevel: {
      type: Array as PropType<IdictValuesItemNode[]>,
      default: () => [],
    },
    isEditState: {
      type: Boolean,
      default: false,
    },
    isChangePrototypeOrder: {
      type: Boolean,
      default: false,
    },
    isChangeDesignOrder: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const machineFormRef = ref();
    const machineFormData = computed<IMachineData>({
      get() {
        return props.modelValue;
      },
      set(val) {
        emit('update:modelValue', val);
      },
    });
    // 字典相关
    const categoryTreeList = computed(() => props.categoryTree);
    const clothesSizeList = computed(() => props.clothesSize);
    const qualityLevelList = computed(() => props.qualityLevel);
    // 选择标准后的尺码选择
    const sizeList = ref<IdictValuesItemNode[]>();
    // 设计师
    const {
      fetchDesignerLoading,
      designerList,
      fetchDesignerList,
    } = useFuzzyFetchDesigner();

    // 客户信息 - 采购商
    const {
      fuzzyCustomerLoading,
      fuzzyCustomerList,
      fuzzyCustomerRemoteMethod,
    } = useFuzzyCustomerByNameCode();

    // 客户详情
    const {
      // bd信息
      baseInfo,
      // 联系人
      contactsList,
      fetchCustomerDetailById,
    } = useFuzzyCustomerDetail();

    // 选择服务对象
    const handleChangeServiceObject = (id: string) => {
      const item = fuzzyCustomerList.value.find(v => v.purchaserId === id);
      machineFormData.value.serviceObject = item?.purchaserName ?? '';
    };

    // 选择客户时 判断 服务对象
    const handleChangePurchaser = (id: string) => {
      if (id && fuzzyCustomerList.value.length) {
        const currentCustomerItem = fuzzyCustomerList.value.find((item) => {
          return item.purchaserId === id;
        });
        if (currentCustomerItem
          && currentCustomerItem?.isAgency === YES_OR_NO_NUMBER_ENUM.YES) {
          machineFormData.value.serviceObjectId = '';
        }
      }
    };

    // baseInfo 包含了 bd 信息 和 供应商地址
    watch(baseInfo, (info: IPurchaserDetailBaseInfo) => {
      if (info.isAgency !== YES_OR_NO_NUMBER_ENUM.YES) {
        machineFormData.value.serviceObjectId = info?.purchaserId ?? '';
        machineFormData.value.serviceObject = info?.purchaserName ?? '';
      }
      if (!info.bindWorkerId) {
        ElMessage.warning('该客户暂无所属BD，请重新选择');
        contactsList.value = [];
        machineFormData.value.purchaserId = '';
        machineFormData.value.purchaserContactId = '';
        machineFormData.value.purchaserContactMobile = '';
        machineFormData.value.bdId = '';
        machineFormData.value.bdName = '';
        machineFormData.value.bdCode = '';
        machineFormData.value.shippingAddress.postProvince = '';
        machineFormData.value.shippingAddress.postCity = '';
        machineFormData.value.shippingAddress.postArea = '';
        machineFormData.value.shippingAddress.postAddressDetail = '';
        return;
      }
      machineFormData.value.purchaserName = info?.purchaserName ?? '';
      machineFormData.value.bdId = info?.bindWorkerId ?? '';
      machineFormData.value.bdName = info?.bindWorkerName ?? '';
      machineFormData.value.bdCode = info?.bindWorkerCode ?? '';
      machineFormData.value.shippingAddress.postProvince = info.province;
      machineFormData.value.shippingAddress.postCity = info.city;
      machineFormData.value.shippingAddress.postArea = info.area;
      machineFormData.value.shippingAddress.postAddressDetail = info.address;
    });

    // 当前选中的客户 - 采购商
    watch(() => machineFormData.value.purchaserId, (val: string) => {
      // 搜索客户详情，拿到对应 bd
      if (val) {
        fetchCustomerDetailById(val);
      } else {
        machineFormData.value.purchaserContactId = '';
      }
      if (fuzzyCustomerList.value.length !== 0) {
        // machineFormData.value.purchaserContactId = '';
        const currentCustomerItem = fuzzyCustomerList.value.find((item) => {
          return item.purchaserId === val;
        });
        machineFormData.value.purchaserName = currentCustomerItem?.purchaserName ?? '';
        machineFormData.value.purchaserCode = currentCustomerItem?.purchaserCode ?? '';
      }
    });

    // 联系人处理
    watch(() => machineFormData.value.purchaserContactId, (val) => {
      if (contactsList.value.length) {
        const currentContactItem = contactsList.value?.find((item) => {
          return item.contactId === val;
        });
        machineFormData.value.purchaserContactName = currentContactItem?.contactName ?? '';
        machineFormData.value.purchaserContactMobile = currentContactItem?.contactMobile ?? '';
      }
    });

    // 处理设计师
    watch(() => machineFormData.value.designerId, (val) => {
      if (machineFormData.value.designerName) {
        fetchDesignerList(machineFormData.value.designerName);
      }
      if (designerList.value.length) {
        const currentItem = designerList.value.find((item) => {
          return item.userId === val;
        });
        machineFormData.value.designerName = currentItem?.username ?? '';
        machineFormData.value.designerCode = currentItem?.userCode ?? '';
      }
    });

    // 款式图片处理
    watch(() => machineFormData.value._stylePictureList, (val: IFileData[]) => {
      machineFormData.value.stylePictureList = val?.map(item => item.url) ?? [];
    });

    // 校验款式品类，0.3需求添加必须到四级
    const handleChangeStyleType = (val: string[]) => {
      if (val.length < 4) {
        machineFormData.value._styleType = [];
        machineFormData.value.styleType = '';
        machineFormData.value.styleTypeName = '';
        ElMessage.warning('请选择款式品类到四级');
      }
    };
    // 处理选中的款式品类
    watch([() => machineFormData.value._styleType, categoryTreeList], ([val]) => {
      const valArr = [...val];
      const nameArr: string[] = [];
      let list = [...categoryTreeList.value];
      if (list.length && valArr.length) {
        while (valArr.length) {
          const code = valArr.shift();
          const item = list.find(it => it.valueCode === code);
          nameArr.push(item?.value ?? '');
          list = item?.children ?? [];
        }
        // 转大货、编辑状态下需要校验是否有对应的品类被禁用了，有的话就清空重新选
        if (val.includes('') || nameArr.includes('')) {
          machineFormData.value.styleType = '';
          machineFormData.value.styleTypeName = '';
          machineFormData.value._styleType = [];
          return;
        }

        // 拼装数据 code 和 name
        machineFormData.value.styleType = val.join('-');
        machineFormData.value.styleTypeName = nameArr.join('-');
      }
    });

    // 校验表单
    const validateForm = async () => {
      const {
        postProvince,
        postCity,
        postArea,
        postAddressDetail,
      } = machineFormData.value.shippingAddress;
      if (
        !postProvince
        || !postCity
        || !postArea
        || !postAddressDetail
      ) {
        const e = '请填写完整收货地址';
        ElMessage.warning(e);
        return Promise.reject(e);
      }
      const validate = await machineFormRef.value.validate();
      return validate;
    };

    // 重置表单
    const resetForm = async () => {
      await machineFormRef.value.resetFields();
    };

    // 数据赋值时查询一次供应商
    watch([machineFormData, baseInfo], () => {
      if (fuzzyCustomerList.value.length === 0 && baseInfo.value.purchaserId) {
        console.log(fuzzyCustomerList.value.length, baseInfo.value.purchaserName);
        // 初始使用客户详情作为下拉数据选项
        fuzzyCustomerList.value = [{
          ...baseInfo.value as unknown as IPurchaserListListItem,
        }];
        const { serviceObjectId = '' } = machineFormData.value;
        if (serviceObjectId
          && fuzzyCustomerList.value.findIndex(v => v.purchaserId === serviceObjectId) < 0) {
          fuzzyCustomerList.value.push({
            purchaserName: machineFormData.value.serviceObject,
            purchaserId: machineFormData.value.serviceObjectId,
            purchaserCode: '',
          } as unknown as IPurchaserListListItem);
        }
      }
    });
    onUnmounted(() => {
      fuzzyCustomerList.value = [];
    });

    return {
      machineFormRef,
      validateForm,
      resetForm,
      machineFormData,

      handleChangeStyleType,
      handleChangeServiceObject,
      handleChangePurchaser,

      PRODUCTION_MODELS_LIST,
      SEASON_LIST,
      PRODUCTION_PATTERN_ENUM,
      PICK_WAY_LIST,
      QUALITY_LEVEL_LIST,
      CLOTHES_UNIT_LIST,
      YES_OR_NO_NUMBER_ENUM,

      sizeList,
      categoryTreeList,
      clothesSizeList,
      qualityLevelList,

      baseInfo,
      contactsList,
      fetchCustomerDetailById,

      fuzzyCustomerLoading,
      fuzzyCustomerList,
      fuzzyCustomerRemoteMethod,

      fetchDesignerLoading,
      designerList,
      fetchDesignerList,

      rules: {
        purchaserId: [{ required: true, message: '请输入客户信息', trigger: ['blur', 'change'] }],
        bdId: [{ required: true, message: '销售BD不能为空', trigger: ['blur', 'change'] }],
        purchaserContactId: [{ required: true, message: '请选择联系人', trigger: ['blur', 'change'] }],
        _styleType: [{ required: true, message: '请选择款式品类', trigger: ['blur', 'change'] }],
        _stylePictureList: [{ required: true, message: '请上传款式图片', trigger: ['blur', 'change'] }],
        qualityLevel: [{ required: true, message: '请选择品质等级', trigger: ['blur', 'change'] }],
        styleColor: [{ required: true, message: '请输入款式颜色', trigger: ['blur', 'change'] }],
        productionNum: [{ required: true, message: '请输入单款数量', trigger: ['blur', 'change'] }],
        productionUnit: [{ required: true, message: '请选择单款单位', trigger: ['blur', 'change'] }],
        pickupFabricWay: [{ required: true, message: '请选择领料方式', trigger: ['blur', 'change'] }],
        purchaserExpectQuoPrice: [{ required: true, message: '请输入期望报价', trigger: ['blur', 'change'] }],
        requireDeliveryTimeLowerLimit: [{ required: true, message: '请输入货期要求', trigger: ['blur', 'change'] }],
        requireDeliveryTimeUpperLimit: [{ required: true, message: '请输入货期要求', trigger: ['blur', 'change'] }],
        shippingAddress: [{ required: true, message: '请选择收货地址', trigger: ['blur', 'change'] }],
        clothingSizeType: [{ required: true, message: '请选择类型', trigger: ['blur', 'change'] }],
        clothingSizeLowerLimit: [{ required: true, message: '请选择范围', trigger: ['blur', 'change'] }],
        clothingSizeUpperLimit: [{ required: true, message: '请选择范围', trigger: ['blur', 'change'] }],
      },
    };
  },
});
